export interface QualityCount {
  qcNumber: number;
  lastName: string;
  firstName: string;
  preferredName: string | null;
  dob: string;
  age: number;
  offset: number;
  phone: string;
  email: string;
  fourHClubID: number;
  fourHCounty: string;
  fourHName: string;
  ffaClubID: number;
  ffaCounty: string;
  ffaName: string;
  currentGrade: number;
  requiredLevel: string;
  lastTestGrade: number;
  version: number;
  level: string;
  score: number;
  contentDate: string | null;
  content: boolean;
  testDate: string | null;
  complete: boolean;
  createDate: string;
  active: boolean | null;
  qcid: number;
  importFile: string;
  importedDate: string;
  importedBy: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
}

export interface QualityCountResponse {
  data: QualityCount;
  success: boolean;
  statusCode: number;
  message: string | null;
  errorDetails: string | null;
}

/**
 * The special bypass value that allows exhibitors to proceed without a verified
 * QC number. This value unlocks Junior Market/Breeding divisions even when the
 * exhibitor doesn't have a real Quality Counts number.
 */
export const QC_BYPASS_VALUE = '000000';

/** Shown when the user declines to continue with an unverified number. */
export const QC_INVALID_MESSAGE = `Please enter a valid Quality Counts number or ${QC_BYPASS_VALUE}.`;

/**
 * A stored Quality Counts value → the number to show, or `null` for "none".
 *
 * Records carry several spellings of "no number": empty, and all-zero
 * placeholders of varying width ('0', '00000') written by older versions of
 * the form. Most mean the exhibitor has no QC number, so the question answers
 * "No" rather than sitting unanswered.
 *
 * The special value '000000' (six zeros) is preserved as a bypass that allows
 * exhibitors to proceed and unlock divisions even without a verified QC.
 */
export function storedQualityCounts(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (!text) return null;
  if (text === QC_BYPASS_VALUE) return text; // Preserve bypass value
  if (/^0+$/.test(text)) return null; // Other all-zero patterns still null
  return text;
}

/** The QC number isn't on file — the caller offers to continue unverified. */
export class QualityCountNotFoundError extends Error {
  constructor() {
    super('QualityCountNotFound');
    this.name = 'QualityCountNotFoundError';
  }
}

/**
 * Pull the record out of a QC lookup response.
 *
 * The endpoint reports "not found" as **HTTP 200 with `success: false` and
 * `statusCode: 404`** rather than a 404 response, so the envelope — not the HTTP
 * status — is what decides. Pass `null` for a genuine HTTP 404.
 */
export function unwrapQualityCount(response: QualityCountResponse | null): QualityCount {
  if (response?.data && response.success !== false) {
    return response.data;
  }
  if (response && response.statusCode != null && response.statusCode !== 404) {
    throw new Error(response.message || response.errorDetails || 'Quality Counts lookup failed.');
  }
  throw new QualityCountNotFoundError();
}
